/**
 * CHI-31: admin view of ticket orders that need a person to look at them (partial refund,
 * dispute, paid after expiry, revoked approval, special-event payment). From here an admin can
 * revoke one ticket (its QR stops working and the holder is emailed), mark the flag resolved with a
 * note, or open the payment in Stripe. Nothing here refunds money: refunds happen in Stripe.
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";

const card = { border: "1px solid rgba(255,255,255,.12)", borderRadius: 12, padding: "1rem", background: "rgba(15,23,42,.7)", marginBottom: "1rem" } as const;
const btn = { minHeight: 40, padding: ".4rem .9rem", borderRadius: 8, cursor: "pointer", border: "1px solid rgba(255,255,255,.2)", background: "transparent", color: "#E2E8F0", fontSize: "1rem" } as const;
const field = { minHeight: 40, padding: ".4rem .6rem", borderRadius: 8, border: "1px solid rgba(255,255,255,.2)", background: "#0F172A", color: "#E2E8F0", fontSize: "1rem", width: "100%", boxSizing: "border-box" } as const;

const money = (cents: number | null, currency: string) =>
  cents == null ? "Amount unknown" : new Intl.NumberFormat("en-US", { style: "currency", currency: currency.toUpperCase() }).format(cents / 100);

export default function TicketFlagsView() {
  const utils = trpc.useUtils();
  const flags = trpc.events.ticketFlagsReview.useQuery();
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [revokeFor, setRevokeFor] = useState<number | null>(null);
  const [resolveFor, setResolveFor] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const done = async (text: string) => {
    setMessage({ kind: "ok", text }); setRevokeFor(null); setResolveFor(null); setNote("");
    await utils.events.ticketFlagsReview.invalidate();
  };
  const fail = (e: { message: string }) => setMessage({ kind: "error", text: e.message });
  const revoke = trpc.events.revokeTicket.useMutation({ onSuccess: () => done("Ticket revoked. Its QR no longer works and the holder was notified."), onError: fail });
  const resolve = trpc.events.resolveTicketFlag.useMutation({ onSuccess: () => done("Flag resolved and the note was saved."), onError: fail });

  return (
    <section aria-labelledby="ticket-flags-title" style={{ padding: "clamp(1rem,3vw,2rem)", color: "#E2E8F0", fontFamily: "'Inter', sans-serif", overflowY: "auto" }}>
      <header style={{ marginBottom: "1.25rem" }}>
        <p style={{ margin: 0, color: "#F9A8D4", fontSize: ".75rem", letterSpacing: ".14em", textTransform: "uppercase" }}>Ticket flags</p>
        <h1 id="ticket-flags-title" style={{ margin: ".25rem 0 .5rem", color: "#FFFFFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(1.9rem,4vw,2.6rem)", fontWeight: 400 }}>Orders that need a decision</h1>
        <p style={{ margin: 0, maxWidth: 680, color: "#94A3B8", lineHeight: 1.6, fontSize: "1rem" }}>
          Revoking a ticket turns off its QR and emails the holder. It does not refund anything; refund in Stripe, then resolve the flag with a note.
        </p>
      </header>

      <div role="status" aria-live="polite">
        {message && <p style={{ margin: "0 0 1rem", color: message.kind === "ok" ? "#86EFAC" : "#FCA5A5" }}>{message.text}</p>}
      </div>

      {flags.isLoading && <p>Loading flagged orders…</p>}
      {flags.error && <p style={{ color: "#FCA5A5" }}>Could not load flagged orders: {flags.error.message}</p>}
      {flags.data && flags.data.length === 0 && <p style={{ color: "#94A3B8" }}>No open flags. Everything is settled.</p>}

      {flags.data?.map(order => (
        <article key={order.orderId} style={card} aria-label={`Order ${order.orderId}`}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: ".75rem", justifyContent: "space-between", alignItems: "baseline" }}>
            <h2 style={{ margin: 0, fontSize: "1.05rem", color: "#FFFFFF" }}>Order {order.orderId} · {order.eventSlug}</h2>
            <strong style={{ color: "#F9A8D4" }}>{money(order.amountTotalCents, order.currency)}</strong>
          </div>
          <p style={{ margin: ".5rem 0", color: "#FCD34D" }}>{order.reason}</p>
          <p style={{ margin: ".25rem 0", color: "#CBD5E1", fontSize: "1rem" }}>
            Buyer: {order.buyerName ?? "Unknown"}{order.buyerEmail ? ` · ${order.buyerEmail}` : ""} · {order.quantity} ticket{order.quantity === 1 ? "" : "s"}
            {order.disputed ? " · disputed" : ""}
          </p>

          <ul style={{ listStyle: "none", margin: ".75rem 0", padding: 0, display: "grid", gap: ".5rem" }}>
            {order.tickets.map(t => (
              <li key={t.ticketId} style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", justifyContent: "space-between", alignItems: "center", padding: ".5rem .75rem", borderRadius: 8, background: "rgba(255,255,255,.04)" }}>
                <span style={{ fontSize: "1rem" }}>
                  {t.attendeeName}{t.attendeeEmail ? ` · ${t.attendeeEmail}` : ""}
                  <span style={{ marginLeft: ".5rem", color: t.status === "active" ? "#86EFAC" : "#94A3B8" }}>{t.status}{t.statusReason ? ` (${t.statusReason.replace(/_/g, " ")})` : ""}</span>
                  {t.checkedInAt ? <span style={{ marginLeft: ".5rem", color: "#93C5FD" }}>checked in</span> : null}
                </span>
                {(t.status === "active" || t.status === "suspended") && (
                  revokeFor === t.ticketId ? (
                    <span style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", alignItems: "center" }}>
                      <input aria-label="Revoke note (optional)" placeholder="Note (optional)" value={note} onChange={e => setNote(e.target.value)} style={{ ...field, width: 220 }} />
                      <button type="button" style={{ ...btn, borderColor: "#F87171", color: "#FCA5A5" }} disabled={revoke.isPending}
                        onClick={() => revoke.mutate({ ticketId: t.ticketId, note: note.trim() || undefined })}>Confirm revoke</button>
                      <button type="button" style={btn} onClick={() => { setRevokeFor(null); setNote(""); }}>Cancel</button>
                    </span>
                  ) : (
                    <button type="button" style={btn} onClick={() => { setRevokeFor(t.ticketId); setResolveFor(null); setNote(""); }}>Revoke ticket</button>
                  )
                )}
              </li>
            ))}
            {order.tickets.length === 0 && <li style={{ color: "#94A3B8", fontSize: "1rem" }}>No tickets were issued for this order.</li>}
          </ul>

          <div style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", alignItems: "center" }}>
            {order.stripePaymentUrl
              ? <a href={order.stripePaymentUrl} target="_blank" rel="noopener noreferrer" style={{ ...btn, display: "inline-flex", alignItems: "center", textDecoration: "none" }}>Open payment in Stripe</a>
              : <span style={{ color: "#94A3B8", fontSize: "1rem" }}>No Stripe payment on file</span>}
            {resolveFor === order.orderId ? (
              <>
                <input aria-label="Resolution note" placeholder="What was done? (required)" value={note} onChange={e => setNote(e.target.value)} style={{ ...field, flex: "1 1 240px", width: "auto" }} />
                <button type="button" style={{ ...btn, borderColor: "#86EFAC", color: "#86EFAC" }} disabled={resolve.isPending || note.trim().length < 3}
                  onClick={() => resolve.mutate({ orderId: order.orderId, note: note.trim() })}>Save and resolve</button>
                <button type="button" style={btn} onClick={() => { setResolveFor(null); setNote(""); }}>Cancel</button>
              </>
            ) : (
              <button type="button" style={btn} onClick={() => { setResolveFor(order.orderId); setRevokeFor(null); setNote(""); }}>Mark resolved</button>
            )}
          </div>
        </article>
      ))}
    </section>
  );
}
